"use client";

import { CNSWorkspaceCreditBalance } from "@/models/cns";
import Link from "next/link";
import { cn } from "@/lib/utils";

interface CreditUsageBarProps {
  balance: CNSWorkspaceCreditBalance | null;
  /** @deprecated Use variant instead */
  compact?: boolean;
  variant?: "default" | "inline";
  className?: string;
}

function toTitleCase(value: string): string {
  return value
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function CreditUsageBar({
  balance,
  compact = false,
  variant,
  className,
}: CreditUsageBarProps) {
  if (!balance) return null;

  const resolvedVariant = variant ?? (compact ? "inline" : "default");
  const planLabel = balance.planTier ? toTitleCase(balance.planTier) : "Unknown";
  const planTotalLabel = balance.unlimited
    ? "∞"
    : (balance.monthlyPlanCredits ?? 0).toLocaleString();
  const availableLabel = balance.unlimited
    ? "∞"
    : (balance.totalCreditsRemaining ?? 0).toLocaleString();

  if (resolvedVariant === "inline") {
    return (
      <Link
        href="/dashboard/billing"
        className={cn(
          "inline-flex h-9 max-w-[9.5rem] shrink-0 items-center rounded-md border bg-card px-2.5 transition-colors hover:bg-muted/60",
          className,
        )}
        aria-label={`${planLabel} plan: ${availableLabel} of ${planTotalLabel} credits remaining. Open billing.`}
        title={`${availableLabel}/${planTotalLabel} credits`}
      >
        <span className="flex min-w-0 items-center gap-1.5 overflow-hidden text-[11px]">
          <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 font-medium">
            {planLabel}
          </span>
          <span className="shrink-0 tabular-nums font-semibold">
            {availableLabel}/{planTotalLabel}
          </span>
        </span>
      </Link>
    );
  }

  return (
    <Link
      href="/dashboard/billing"
      className={cn(
        "flex w-full max-w-full items-center rounded-full border bg-card px-3 py-1.5 transition-colors hover:bg-muted/60",
        className,
      )}
      aria-label="Open billing"
    >
      <div className="flex min-w-0 items-center gap-2 overflow-hidden text-xs">
        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium">
          {planLabel}
        </span>
        <span className="shrink-0 tabular-nums font-medium">
          {availableLabel}/{planTotalLabel}
        </span>
      </div>
    </Link>
  );
}
